import { ClientOnly } from '../common/client-only';
import FactionCharts from '../factionProfile/FactionCharts';
import FactionMobileStats from '../factionProfile/FactionMobileStats';
import RarityMenu from './RarityDropdown';
import SuperstarAvatar from './SuperstarAvatar';
import RangeSlider from '../rangeSlider';
import SuperstarDetailsTotals from './SuperstarDetailsTotals';
import SuperstarGem from './SuperstarGem';
import Image from 'next/image';
import SuperstarAbility from './SuperstarAbility';
import {
  renderWrestlerGemImage,
  getYouTubeEmbedUrl,
} from '@wwe-portal/web/utils/superstar-details/helper';
import TierDropdown from './TierDropdown';
import { cn } from '@wwe-portal/ui/lib/lib';
import { useTranslations } from 'next-intl';
import WrestlerDetailsTable from './WrestlerDetailsTable';
import SuperstarReviewItem from './SuperstarReview';
import SuperstarSkeleton from './SuperstarSkeleton';
import DetailsHeadingInfo from './DetailsHeadingInfo';
import { SuperstarAvatarProps } from '@wwe-portal/entities/superstar.type';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';

const SuperstarContentDetails = ({
  wrestlerDetails,
  chart1Data,
  XLabel,
  chart2Data,
  rangeSliderValue,
  handleWrestlerLevelChange,
  rangeSliderMaxValue,
  globalDetails,
  abilities,
  handleAbilityEquipChange,
  handleAbilityLevelChange,
  wrestlerTier,
  starRarity,
  disableEquipButton,
  starRarityDropdownItemClick,
  wrestlerTierDropdownItemClick,
  videoLinks,
  avatarData,
  isLoading,
}: {
  wrestlerDetails?: any;
  chart1Data: any;
  XLabel?: any;
  chart2Data: any;
  rangeSliderValue: number;
  handleWrestlerLevelChange: (value: number) => void;
  rangeSliderMaxValue: number;
  globalDetails: any;
  abilities: any;
  handleAbilityEquipChange: (
    flag: boolean,
    ability_id: string,
    level: number
  ) => void;
  handleAbilityLevelChange: (ability_id: string, level: number) => void;
  wrestlerTier: string;
  starRarity: string;
  disableEquipButton: boolean;
  starRarityDropdownItemClick: (level: string) => void;
  wrestlerTierDropdownItemClick: (tier: 'Bronze' | 'Silver' | 'Gold') => void;
  videoLinks: any[];
  avatarData: SuperstarAvatarProps;
  isLoading: boolean;
}) => {
  const t = useTranslations();
  const { isMobile } = useResponsive();

  return (
    <div className="box-border">
      <div className="flex">
        <div className="flex-basis-0 relative box-border w-full flex-grow md:max-w-[250px]">
          <SuperstarAvatar avatarData={avatarData} />
        </div>

        {isLoading && <SuperstarSkeleton />}

        {!isLoading && wrestlerDetails && (
          <div className="flex-basis-0 relative box-border w-full max-w-full flex-grow pl-3.5 pr-3.5">
            {/* Desktop only heading info */}
            <div className="hidden md:block">
              <DetailsHeadingInfo
                name={avatarData.name}
                description={avatarData.description}
              />
            </div>

            {/* Video Reviews */}
            {videoLinks.filter((item) => item.link).length ? (
              <div className="mb-[30px]">
                <h4 className="mb-[30px] text-xl">{t('superstar_reviews')}</h4>
                <div className="md:flex md:flex-wrap">
                  {videoLinks
                    .filter((item) => item.link)
                    .map((item) => {
                      const embedLink = getYouTubeEmbedUrl(item.link);
                      return (
                        <div
                          key={item.link}
                          className="bg-back400 mb-5 rounded-lg p-[10px] md:mb-5 md:basis-1/3 md:bg-transparent md:py-[10px] md:pr-[10px]"
                        >
                          <div className="md:bg-back400 md:rounded-lg md:p-[10px]">
                            <SuperstarReviewItem
                              link={embedLink}
                              image={item.image}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ) : null}

            {/* Charts */}
            <div className="3xl:flex-row relative box-border flex w-full max-w-full flex-col gap-8">
              <ClientOnly>
                {isMobile ? (
                  <>
                    <FactionMobileStats
                      chart={chart1Data as any}
                      isTotalUpgrade={true}
                      isDiagram={false}
                      totalWins={[]}
                      totalLosses={[]}
                    />
                    <FactionMobileStats
                      chart={chart2Data as any}
                      isDiagram={false}
                      totalWins={[]}
                      totalLosses={[]}
                    />
                  </>
                ) : (
                  <>
                    {!!chart1Data?.data?.length && (
                      <FactionCharts
                        chart={chart1Data as any}
                        xLabels={XLabel}
                        isSuperstar={true}
                        isTotalUpgrade={true}
                      />
                    )}
                    {!!chart2Data?.data?.length && (
                      <FactionCharts
                        chart={chart2Data as any}
                        xLabels={XLabel}
                        isSuperstar={true}
                      />
                    )}
                  </>
                )}
              </ClientOnly>
            </div>

            {/* Controls */}
            <div className="mt-5 flex w-full flex-col md:my-10 md:grid md:grid-cols-12 md:gap-5">
              <div className="3xl:col-span-4 w-full flex-col md:col-span-12 md:col-span-6 md:flex">
                <div className="text-inlineTextColor18 mb-1">Select Stars</div>
                <RarityMenu
                  wrestlerTier={wrestlerTier}
                  starRarityDropdownItemClick={starRarityDropdownItemClick}
                  starRarity={starRarity}
                  className="md:h-13"
                />
              </div>

              <div className="3xl:col-span-3 my-5 flex w-full flex-col md:col-span-12 md:col-span-6 md:my-0">
                <div className="text-inlineTextColor18 mb-1">
                  Select Star Color
                </div>
                <TierDropdown
                  wrestlerTier={wrestlerTier}
                  wrestlerTierDropdownItemClick={wrestlerTierDropdownItemClick}
                  className="md:h-13"
                />
              </div>

              <div className="3xl:col-span-5 flex w-full flex-col md:col-span-12">
                <div className="text-inlineTextColor18 mb-1">
                  {t('superstar_level')}
                </div>
                <div className="h-13 relative box-border w-full max-w-full flex-grow rounded bg-[#272b4a] p-1 px-3 md:px-3">
                  <RangeSlider
                    value={rangeSliderValue}
                    min={1}
                    onRangeSliderChange={handleWrestlerLevelChange}
                    max={rangeSliderMaxValue}
                    data-id={'superstar_level_range_slider'}
                  />
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="3xl:grid-cols-4 md:mt-[10px] md:grid md:grid-cols-1 md:gap-5">
              <SuperstarDetailsTotals
                hp={globalDetails?.HP ?? 0}
                talent={globalDetails ? globalDetails.TalentScore : 0}
              />

              <div className="3xl:col-span-3 bg-back400 mt-5 flex w-full flex-wrap items-center justify-between gap-y-2 rounded-md p-5 md:mt-0 md:flex-nowrap">
                {globalDetails && Object.keys(globalDetails).length ? (
                  Object.entries(globalDetails.Gem).map(([key, value]) => (
                    <SuperstarGem
                      key={key}
                      icon={key}
                      score={(value as any) || 0}
                    />
                  ))
                ) : (
                  <div className="coming-soon-data align-items-center flex">
                    {
                      <Image
                        src="./assets/images/coming-soon.svg"
                        alt="coming soon"
                        width={30}
                        height={30}
                        className="rounded-full"
                      />
                    }
                    <div>
                      <h5 className="text-inlineTextColor6 mb-10">
                        {t('data_coming_soon')}
                      </h5>
                      <p className="text-inlineTextColor18 m-0">
                        {t('data_coming_soon_description')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Abilities */}
            {Boolean(abilities.length) && (
              <div>
                <h4 className="py-[30px] text-xl">{t('abilities')}</h4>
                {abilities.map((ability, index) => (
                  <SuperstarAbility
                    abilityID={ability.ID}
                    abilityMovePoint={ability.MovePoint}
                    abilityMaxLevel={ability.levelCap}
                    abilityName={ability.name}
                    abilityGemImage={
                      renderWrestlerGemImage(ability.gemType) ?? ''
                    }
                    onAbilityEquipChange={handleAbilityEquipChange}
                    onAbilityLevelChange={handleAbilityLevelChange}
                    key={ability.ID}
                    isEquip={index > 2}
                    tier={wrestlerTier}
                    starRarity={starRarity}
                    wrestlerData={wrestlerDetails}
                    isDisable={disableEquipButton}
                  />
                ))}
              </div>
            )}

            {/* Tables */}
            {wrestlerDetails?.trainer?.levels ? (
              <WrestlerDetailsTable
                details={wrestlerDetails.trainer.levels}
                title={'trainer'}
              />
            ) : null}
            {wrestlerDetails?.coach?.levels ? (
              <WrestlerDetailsTable
                details={wrestlerDetails.coach.levels}
                title={'coach'}
              />
            ) : null}
            {wrestlerDetails?.mentor?.levels ? (
              <WrestlerDetailsTable
                details={wrestlerDetails.mentor.levels}
                title={'mentor'}
              />
            ) : null}

            {/* Tag Team Links */}
            <div>
              <h4 className="my-[30px] text-xl">{t('tag_team_links')}</h4>
              <div className="rounded-md bg-[#1f2138] p-5">
                <div className="mb-[15px] pl-[15px] pr-[15px]">
                  <div className="flex">
                    <div className="text-uppercase text-text700 w-[100px] min-w-[100px] text-xs font-semibold">
                      {t('link')}
                    </div>
                    <div className="text-uppercase text-text700 text-xs font-semibold">
                      {t('buff')}
                    </div>
                  </div>
                </div>

                <div className="board-body">
                  {wrestlerDetails?.TagTeamLinks?.length
                    ? wrestlerDetails.TagTeamLinks?.map((item, index) => (
                        <div
                          className={cn('board-body-row p-[15px]', {
                            'bg-back400 rounded-md p-[15px]': index % 2 === 0,
                          })}
                          key={item.Name + index}
                        >
                          <div className="flex items-center text-xs">
                            <div className="text-text500 flex w-[100px] min-w-[100px] pr-3">
                              {item.Name}
                            </div>
                            <div className="text-text500 flex items-center">
                              {item.Description?.includes('<affect_type>')
                                ? item.Description.replace(
                                    '<affect_type>',
                                    'Moves'
                                  )
                                : item.Description}
                            </div>
                          </div>
                        </div>
                      ))
                    : null}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperstarContentDetails;
